// ClientKnowledge — umbrella for reference documents.
// Tabs: Governance, Advice, Continuity Manual.
// Deep-link supported via ?view=advice / ?view=continuity.
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { FileText, BookOpen, FileCheck } from 'lucide-react';
import ClientGovernance from './ClientGovernance';
import ClientAdvice from './ClientAdvice';
import ClientContinuityManual from './ClientContinuityManual';

const TABS = [
  { id: 'governance', label: 'Governance', icon: FileText },
  { id: 'advice', label: 'Advice', icon: BookOpen },
  { id: 'continuity', label: 'Continuity Manual', icon: FileCheck },
];

export default function ClientKnowledge() {
  const [params, setParams] = useSearchParams();
  const fromUrl = (params.get('view') || '').toLowerCase();
  const initial = TABS.some((t) => t.id === fromUrl) ? fromUrl : 'governance';
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const next = (params.get('view') || '').toLowerCase();
    if (TABS.some((t) => t.id === next) && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleTab = (id) => {
    setTab(id);
    if (id === 'governance') params.delete('view'); else params.set('view', id);
    setParams(params, { replace: true });
  };

  return (
    <div className="animate-fade-in-up" data-testid="knowledge-hub">
      <div className="flex items-center gap-1 border-b border-[#E6E0D9] mb-4" data-testid="knowledge-hub-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => handleTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors',
                active
                  ? 'border-[#4A3B32] text-[#2C2420] font-medium'
                  : 'border-transparent text-[#5D524D] hover:text-[#2C2420]'
              )}
              data-testid={`knowledge-hub-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'advice' && <ClientAdvice />}
      {tab === 'continuity' && <ClientContinuityManual />}
      {tab === 'governance' && <ClientGovernance />}
    </div>
  );
}
